import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getPayrollLaborReportQueryOptions } from "@/api/financial";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useEstablishment } from "@/hooks/useEstablishment";
import { formatMoney } from "@/utils/financial";
import { PayrollGate } from "../-components/PayrollGate";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/financial/payroll/labor/",
)({
	component: PayrollLaborPage,
});

function PayrollLaborPage() {
	const { orgId } = Route.useParams();
	return (
		<PayrollGate orgId={orgId}>
			<LaborContent orgId={orgId} />
		</PayrollGate>
	);
}

function startOfYearInputValue(): string {
	return `${new Date().getFullYear()}-01-01`;
}

function todayInputValue(): string {
	const now = new Date();
	const month = `${now.getMonth() + 1}`.padStart(2, "0");
	const day = `${now.getDate()}`.padStart(2, "0");
	return `${now.getFullYear()}-${month}-${day}`;
}

function LaborContent({ orgId }: { orgId: string }) {
	const { t } = useTranslation();
	const { establishments } = useEstablishment();
	const [from, setFrom] = useState(startOfYearInputValue());
	const [to, setTo] = useState(todayInputValue());

	const establishmentName = (id: string | null) => {
		if (!id) return t("financial.payroll.labor.orgLevel");
		return establishments.find((e) => e.id === id)?.name ?? id;
	};

	const params = useMemo(
		() => ({
			from: new Date(`${from}T00:00:00.000Z`).toISOString(),
			to: new Date(`${to}T23:59:59.999Z`).toISOString(),
		}),
		[from, to],
	);

	const { data, isLoading } = useQuery(
		getPayrollLaborReportQueryOptions(orgId, params),
	);

	const currency = data?.currency ?? "BRL";

	return (
		<div className="flex flex-col gap-4">
			<PageHeader title={t("financial.payroll.labor.title")} showBackButton />

			<div className="flex flex-col gap-2 sm:flex-row sm:items-end">
				<div className="space-y-1">
					<Label>{t("financial.payroll.labor.from")}</Label>
					<Input
						type="date"
						value={from}
						onChange={(e) => setFrom(e.target.value)}
					/>
				</div>
				<div className="space-y-1">
					<Label>{t("financial.payroll.labor.to")}</Label>
					<Input
						type="date"
						value={to}
						onChange={(e) => setTo(e.target.value)}
					/>
				</div>
			</div>

			{isLoading ? (
				<div className="flex justify-center py-16">
					<LoadingSpinner />
				</div>
			) : !data || data.byWorker.length === 0 ? (
				<Card>
					<CardContent className="py-12 text-center text-sm text-base-content/60">
						{t("financial.payroll.labor.empty")}
					</CardContent>
				</Card>
			) : (
				<>
					<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
						<StatBox
							label={t("financial.payroll.runs.grid.gross")}
							value={formatMoney(data.grossTotal, currency)}
						/>
						<StatBox
							label={t("financial.payroll.runs.grid.encargos")}
							value={formatMoney(data.encargosTotal, currency)}
						/>
						<StatBox
							label={t("financial.payroll.runs.grid.net")}
							value={formatMoney(data.netTotal, currency)}
						/>
					</div>

					<Card>
						<CardHeader>
							<CardTitle className="text-base">
								{t("financial.payroll.labor.byWorker")}
							</CardTitle>
						</CardHeader>
						<CardContent className="p-0">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>
											{t("financial.payroll.workers.columns.name")}
										</TableHead>
										<TableHead className="hidden sm:table-cell">
											{t("financial.payroll.workers.columns.employmentType")}
										</TableHead>
										<TableHead className="text-right">
											{t("financial.payroll.runs.grid.gross")}
										</TableHead>
										<TableHead className="text-right hidden md:table-cell">
											{t("financial.payroll.runs.grid.encargos")}
										</TableHead>
										<TableHead className="text-right">
											{t("financial.payroll.runs.grid.net")}
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{data.byWorker.map((w) => (
										<TableRow key={w.workerId}>
											<TableCell className="font-medium">
												{w.workerName}
											</TableCell>
											<TableCell className="hidden sm:table-cell text-base-content/70">
												{t(
													`financial.payroll.employmentTypes.${w.employmentType}`,
												)}
											</TableCell>
											<TableCell className="text-right">
												{formatMoney(w.grossTotal, currency)}
											</TableCell>
											<TableCell className="text-right hidden md:table-cell">
												{formatMoney(w.encargosTotal, currency)}
											</TableCell>
											<TableCell className="text-right font-medium">
												{formatMoney(w.netTotal, currency)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</CardContent>
					</Card>

					<Card>
						<CardHeader>
							<CardTitle className="text-base">
								{t("financial.payroll.labor.byFarm")}
							</CardTitle>
						</CardHeader>
						<CardContent className="p-0">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>{t("financial.payroll.labor.farm")}</TableHead>
										<TableHead className="text-right">
											{t("financial.payroll.runs.grid.gross")}
										</TableHead>
										<TableHead className="text-right hidden md:table-cell">
											{t("financial.payroll.runs.grid.encargos")}
										</TableHead>
										<TableHead className="text-right">
											{t("financial.payroll.runs.grid.net")}
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{data.byFarm.map((f) => (
										<TableRow key={f.establishmentId ?? "__org__"}>
											<TableCell className="font-medium">
												{establishmentName(f.establishmentId)}
											</TableCell>
											<TableCell className="text-right">
												{formatMoney(f.grossTotal, currency)}
											</TableCell>
											<TableCell className="text-right hidden md:table-cell">
												{formatMoney(f.encargosTotal, currency)}
											</TableCell>
											<TableCell className="text-right font-medium">
												{formatMoney(f.netTotal, currency)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</CardContent>
					</Card>
				</>
			)}
		</div>
	);
}

function StatBox({ label, value }: { label: string; value: string }) {
	return (
		<Card>
			<CardContent className="p-4">
				<p className="text-sm text-base-content/60">{label}</p>
				<p className="mt-1 text-xl font-semibold">{value}</p>
			</CardContent>
		</Card>
	);
}
